import { useState } from 'react';
import { Ghost } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { post, ApiError } from '@/lib/api';

export default function Login() {
  const [email, setEmail] = useState('');
  const [code, setCode] = useState('');
  const [stage, setStage] = useState<'email' | 'code'>('email');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  async function sendCode(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await post('/api/auth/send-code', { email });
      setStage('code');
    } catch (err) {
      setError(err instanceof ApiError ? `Couldn't send code (${err.status})` : 'Network error');
    } finally { setBusy(false); }
  }

  async function verifyCode(e: React.FormEvent) {
    e.preventDefault();
    setBusy(true); setError(null);
    try {
      await post('/api/auth/verify-code', { email, code });
      // Hard redirect (not client-side nav): forces a fresh app load so the
      // /api/auth/me SWR cache re-fetches with the new session cookie. Client
      // nav reused the pre-login 401 (shouldRetryOnError:false), which bounced
      // the route guard straight back to /login. Mirrors logout()'s redirect.
      window.location.href = '/';
    } catch (err) {
      setError(err instanceof ApiError ? 'Invalid code' : 'Network error');
    } finally { setBusy(false); }
  }

  return (
    <div className="flex h-full items-center justify-center bg-bg bg-[radial-gradient(1100px_520px_at_50%_-12%,rgba(0,122,255,0.10),transparent_62%)] p-6">
      <div className="w-full max-w-[372px] animate-pop-in rounded-[18px] border border-black/[0.05] bg-surface p-9 shadow-raised">
        <div className="mb-6 flex justify-center">
          <div className="flex h-12 w-12 items-center justify-center rounded-[12px] bg-gradient-to-b from-[#3a9bff] to-primary shadow-glow">
            <Ghost className="h-6 w-6 text-white" strokeWidth={2.1} aria-hidden />
          </div>
        </div>
        <h1 className="text-center text-[19px] font-semibold tracking-tighter">GhostFit Console</h1>
        <p className="mt-1 text-center text-[12px] text-muted">
          {stage === 'email' ? 'Sign in with your work email' : `Code sent to ${email}`}
        </p>

        {stage === 'email' ? (
          <form onSubmit={sendCode} className="mt-7 space-y-3">
            <label htmlFor="console-login-email" className="sr-only">Work email</label>
            <Input
              id="console-login-email"
              type="email" value={email} onChange={(e) => setEmail(e.target.value)}
              placeholder="you@ghostfitsports.com" required autoFocus
              className="h-9 text-[13px]"
            />
            <Button type="submit" disabled={busy} size="lg" className="w-full">
              {busy ? 'Sending…' : 'Send code'}
            </Button>
          </form>
        ) : (
          <form onSubmit={verifyCode} className="mt-7 space-y-3">
            <label htmlFor="console-login-code" className="sr-only">Verification code</label>
            <Input
              id="console-login-code"
              type="text" inputMode="numeric" pattern="\d{6}" maxLength={6}
              value={code} onChange={(e) => setCode(e.target.value)}
              placeholder="••••••" required autoFocus
              className="h-10 text-center font-mono text-[18px] tracking-[0.45em] placeholder:tracking-[0.45em]"
            />
            <Button type="submit" disabled={busy} size="lg" className="w-full">
              {busy ? 'Verifying…' : 'Continue'}
            </Button>
            <button
              type="button" onClick={() => { setStage('email'); setCode(''); setError(null); }}
              className="w-full text-center text-[11px] text-muted transition-colors hover:text-text"
            >
              Use a different email
            </button>
          </form>
        )}

        {error && <p role="alert" aria-live="assertive" className="mt-3 text-center text-[11px] text-danger">{error}</p>}

        <p className="mt-7 text-center text-[10px] tracking-[0.02em] text-subtle">
          GhostFit Sports · internal operations
        </p>
      </div>
    </div>
  );
}
